<template>
    <!-- The control, the accessible name and the attention dot are the orb's and are
         written once (AssistantOrb.vue). This component fills its picture — and where
         it cannot, passes nothing and the CSS sphere is drawn instead. -->
    <AssistantOrb
        :state="state"
        :size="size"
        :decorative="decorative"
        @click="emit('click')"
    >
        <template v-if="available" #default>
            <!-- The frame is what the bubble hangs on: the window below clips everything
                 to the circle, and a bubble drawn inside it was never seen. -->
            <span class="milo-frame" :style="headStyle">
            <span class="milo-head" :style="headStyle">
                <!-- The model's own frame, larger than the circle and sitting high in
                     it, so that what the circle shows is the FACE. See HEAD_SCALE. -->
                <span class="milo-stage" :style="stageStyle">
                    <span ref="mount" class="milo-layer" />

                    <!-- The poster, inside the same frame so that the picture does not
                         jump the moment the model takes over from it. -->
                    <img
                        v-if="!loaded && !posterFailed"
                        :key="posterUrl"
                        class="milo-layer"
                        :src="posterUrl"
                        :alt="$t('assistant.head_loading')"
                        @error="onPosterError"
                    >
                </span>
            </span>

            <!-- Thinking, said in a way that reads at 32 px: three dots in a bubble on
                 the head's shoulder while a request is in flight. The model plays its
                 own Thinking clip underneath; that is a look, this is a signal. -->
            <span v-if="state === 'thinking'" class="milo-thought" aria-hidden="true">
                <span class="milo-thought-dot" />
                <span class="milo-thought-dot" />
                <span class="milo-thought-dot" />
            </span>
            </span>
        </template>
    </AssistantOrb>
</template>

<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

    import AssistantOrb from '@/components/app/AssistantOrb.vue'
    import { miloStateFor, onMiloReaction, type AssistantOrbState } from '@/libs/assistant'
    import { MILO_ASSET_BASE, miloRendererSupported } from '@/libs/milo'
    import { useMiloGaze } from '@/libs/miloGaze'
    import type { MiloRendererHandle } from '@/libs/miloRenderer'
    import type { MiloReaction } from '@/types/Milo'

    /**
     * Milo's face — the owner's animated 3D head, wherever the orb used to be.
     *
     * # What this is
     *
     * The model is the owner's (`public/milo/2.0.0/`, see `docs/AI.md` § 20): a GLB with
     * ten clips and twelve eye morph targets, plus a poster. What draws it is OURS since
     * v1.456.0 — `libs/miloRenderer.ts`, a small `three` renderer — because the eye
     * morphs are the point and the vendored `<model-viewer>` that used to draw the head
     * offers no way to touch one.
     *
     * The renderer is loaded with a dynamic `import()` from here and nowhere else, which
     * is what keeps `three` out of the entry chunk: the login page and every account
     * without `chat:use` are unchanged by all of this.
     *
     * The generation ticket read on both sides of every await is not house habit and is
     * not to be tidied away: an `import()` cannot be cancelled, so the only defence
     * against a renderer that finishes building after this component is gone is to refuse
     * to use it. Same for `react()` waiting a tick — see below.
     *
     * # The crop
     *
     * The model is framed with a neck stub below the chin. The circle clips, and the
     * model is drawn into a frame `HEAD_SCALE` times as wide, sitting a little high in
     * it: the circle shows the WHOLE head with air around it, and only the neck stub is
     * outside. The first cut (1.4, face filling the circle) read as a filled disc at
     * 40 px — the owner asked for a head one can see is a head.
     *
     * # States and reactions
     *
     *   idle / thinking / attention  — the orb's three states, mapped in
     *                                  `libs/assistant.ts` (`attention` → Concerned).
     *   success / error / smile      — one-shot expressions, from
     *                                  `reactMilo()` on the same composable.
     *
     * Every mounted head subscribes to that bus, so both instances react together.
     *
     * # Looking at the cursor
     *
     * The EYES follow the mouse and the head leans after them, which is the whole reason
     * the renderer was written. `libs/miloGaze.ts` measures the cursor from this head's
     * own box and hands out both answers; the renderer reads it once per drawn frame.
     * Each mounted head measures from its own centre, so both look at the same cursor
     * rather than at the same point.
     *
     * # When there is no head
     *
     * No WebGL, or the renderer chunk could not be fetched, or the model itself failed:
     * nothing fills the orb's slot and the desk sees the CSS sphere, which has been the
     * assistant's face since v1.446.0 and stays for exactly this. Reduced motion is the
     * renderer's business — it holds a still pose rather than animating.
     */

    /**
     * How much larger than the visible circle the model's frame is drawn, and how much
     * of the overflow is taken off the top.
     *
     * 1.1 and a third: at the top bar's 40 px the frame is 44 px, about 1 px of it
     * above the circle and 3 px below it — the crown and the chin stay with a margin
     * of background around them, the neck goes.
     */
    const HEAD_SCALE = 1.1
    const HEAD_RISE  = 0.35

    const props = withDefaults(defineProps<{
        /** The assistant's state, in the portal's own vocabulary. */
        state?: AssistantOrbState
        /** Diameter of the visible circle in pixels. 40 in the top bar, 96 in the
         *  drawer's header. */
        size?: number
        /** A picture of Milo rather than the control — the drawer's own header. */
        decorative?: boolean
    }>(), {
        state:      'idle',
        size:       40,
        decorative: false,
    })

    const emit = defineEmits<{ click: [] }>()

    const mount        = ref<HTMLElement | null>(null)
    /** The model is drawn and the poster can go. */
    const loaded       = ref(false)
    const moduleFailed = ref(false)
    const posterFailed = ref(false)

    /** Where the cursor is, seen from this head. The renderer reads it every frame. */
    const gaze = useMiloGaze()

    /** Asked before anything is downloaded: a machine with no WebGL must not fetch a
     *  renderer and a megabyte of model in order to discover it cannot use them. */
    const rendererSupported = miloRendererSupported()

    const available = computed((): boolean => rendererSupported && !moduleFailed.value)

    // Resolved against the PAGE, not against the hashed bundle chunk this code is running
    // from — the portal is served from the domain root today and this keeps working if it
    // ever is not. The renderer resolves the GLB the same way.
    const posterUrl = computed((): string => new URL(`${MILO_ASSET_BASE}/MILO-Poster.png`, document.baseURI).href)

    const headStyle = computed(() => ({
        width:    `${props.size}px`,
        height:   `${props.size}px`,
        // The thought bubble is sized in em, so it follows the head from 32 to 96 px.
        fontSize: `${props.size}px`,
    }))

    const stageStyle = computed(() => {
        const frame    = props.size * HEAD_SCALE
        const overflow = frame - props.size

        return {
            width:  `${frame}px`,
            height: `${frame}px`,
            left:   `${-overflow / 2}px`,
            top:    `${-overflow * HEAD_RISE}px`,
        }
    })

    let renderer:  MiloRendererHandle | null = null
    let mounted    = false
    let generation = 0

    const teardown = () => {
        gaze.release()
        renderer?.dispose()
        renderer = null
        loaded.value = false
    }

    /** No head, then: the orb takes over and nothing more is attempted. */
    const giveUp = () => {
        teardown()
        moduleFailed.value = true
    }

    const initialize = async () => {
        // The ticket a later mount, or an unmount, invalidates. Read on BOTH sides of
        // every await.
        const ticket = ++generation

        teardown()

        const current = () => mounted && ticket === generation && mount.value !== null

        try {
            // The one dynamic import in the portal that matters for size: everything
            // `three` needs is behind it, in its own chunk.
            const { createMiloRenderer } = await import('@/libs/miloRenderer')

            if (!current()) return

            const host = mount.value!

            gaze.follow(host)

            const handle = await createMiloRenderer({
                host,
                size:    props.size * HEAD_SCALE,
                state:   miloStateFor(props.state),
                offset:  gaze.offset,
                onReady: () => { if (current()) loaded.value = true },
            })

            if (!current()) {
                handle.dispose()

                return
            }

            renderer = handle
            // The latest state, not the one this function started with.
            handle.setState(miloStateFor(props.state))
        } catch {
            if (!current()) return

            giveUp()
        }
    }

    /**
     * Plays one expression on this head.
     *
     * The tick is the point: a caller that ends a request sets the persistent state to
     * `idle` and asks for `success` in the same handler, and the renderer returns to the
     * PERSISTENT state when the expression is over. Without the wait the renderer would
     * still be on `thinking` when the reaction was queued, and Milo would go back to
     * thinking about a request that had finished.
     */
    const react = async (reaction: MiloReaction) => {
        const ticket = generation

        await nextTick()

        if (!mounted || ticket !== generation) return

        renderer?.react(reaction)
    }

    const onPosterError = (event: Event) => {
        // Only the poster this component asked for; a stale error about a URL that is
        // no longer current says nothing about the picture on screen.
        if ((event.target as HTMLImageElement).getAttribute('src') === posterUrl.value) {
            posterFailed.value = true
        }
    }

    watch(() => props.state, state => renderer?.setState(miloStateFor(state)))

    watch(() => props.size, size => renderer?.setSize(size * HEAD_SCALE))

    onMiloReaction(reaction => { void react(reaction) })

    onMounted(() => {
        mounted = true

        if (available.value) void initialize()
    })

    onBeforeUnmount(() => {
        mounted = false
        generation++
        teardown()
    })
</script>

<style scoped>
    /* The thought bubble: sits on the head's top-right shoulder, scales with the head
       (em on the head's font-size, which headStyle sets from `size`), and pulses its
       three dots one after another. Tokens only, so it holds in both themes. */
    .milo-frame {
        position: relative;
        display: block;
        overflow: visible;
    }

    .milo-thought {
        position: absolute;
        top: 0;
        right: -0.3em;
        display: flex;
        gap: max(2px, 0.06em);
        align-items: center;
        /* Small next to a 96 px head, still legible next to a 32 px one: the floor
           is what keeps the bar's bubble from vanishing, the em is what keeps the
           drawer's from covering the face. */
        padding: max(2px, 0.08em) max(3px, 0.12em);
        border-radius: 999px;
        background: var(--ui-bg-elevated);
        border: 1px solid var(--ui-border);
        box-shadow: 0 1px 4px rgb(0 0 0 / 12%);
        z-index: 2;
    }

    .milo-thought-dot {
        width: max(3px, 0.1em);
        height: max(3px, 0.1em);
        border-radius: 999px;
        background: var(--ui-primary);
        animation: milo-thought-pulse 1.2s ease-in-out infinite;
    }

    .milo-thought-dot:nth-child(2) { animation-delay: 0.2s; }
    .milo-thought-dot:nth-child(3) { animation-delay: 0.4s; }

    @keyframes milo-thought-pulse {
        0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
        40%           { transform: translateY(-35%); opacity: 1; }
    }

    @media (prefers-reduced-motion: reduce) {
        .milo-thought-dot { animation: none; opacity: 0.8; }
    }

    /* The window. Everything the model does outside it is the reason it exists. */
    .milo-head {
        position: relative;
        display: block;
        overflow: hidden;
        border-radius: 9999px;
    }

    .milo-stage {
        position: absolute;
    }

    .milo-layer {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
</style>
